import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { isEmpty } from '@ember/utils'
import { action } from '@ember/object'
import { SearchBox } from 'teacher-ui-client/components/search-box'
import type Owner from '@ember/owner'

interface Item {
  id: string
  title: string
}

interface Args {
  items: Item[]
  selectedItem?: Item
  includeSelectAll?: boolean
  selectAllName?: string
  placeholderText?: string
  selectAction?: (item: Item) => unknown
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class FilteringSelect extends Component<Signature> {
  constructor(owner: Owner, args: Args) {
    super(owner, args)

    // Initialise the search term as the selected item
    this.searchTerm = this.args.selectedItem?.title ?? ''
  }

  @tracked
  searchTerm: string | null | undefined = ''

  get selectAllOption() {
    const title = this.args.selectAllName ?? 'All items'

    return { id: 'all', title }
  }

  get options() {
    const beforeOptions = this.args.includeSelectAll ? [this.selectAllOption] : []
    const allOptions = [...beforeOptions, ...(this.args.items ?? [])]
    const options = allOptions.filter((opt) => opt.id !== this.args.selectedItem?.id)

    const searchTerm = this.searchTerm?.toLowerCase() || ''
    const selectedOptionName = this.args.selectedItem?.title?.toLowerCase() || ''

    if (isEmpty(searchTerm) || selectedOptionName === searchTerm) {
      return options
    } else {
      return options.filter((option) => option.title.toLowerCase().includes(searchTerm))
    }
  }

  @action
  setSearchTerm(newSearchTerm?: string | null) {
    this.searchTerm = newSearchTerm
  }

  @action
  selectItem(option: Item) {
    this.searchTerm = option.title
    this.args.selectAction?.(option)
  }

  <template>
    <SearchBox
      data-test-filtering-select
      @placeholder={{@placeholderText}}
      @autocomplete={{true}}
      @value={{this.searchTerm}}
      @autocompleteOptions={{this.options}}
      @updateAction={{this.setSearchTerm}}
      @selectOptionAction={{this.selectItem}}
      @showOptionsWhenEmptyValue={{true}}
      ...attributes
      as |autocompleteOption|
    >
      {{autocompleteOption.title}}
    </SearchBox>
  </template>
}

export default FilteringSelect
